import { makeObservable, observable, flow, action, IObservableArray } from "mobx";
import { NotificationStoreInstance } from "@common/stores";
import { IClientModel, AppointmentListingModel, HospitalModel, ReferralTypeModel, ClientModel, IClientNoteModel, PetModel } from "@common/models";
import { ClientNoteService, ClientService } from "@common/services";
import { IAddClientEmailRequest } from "@common/models/requests";
import { AppointmentStatus } from "@common/models/Enums";
import { includes, chain, first, isEmpty } from "lodash";
import { DateTime } from "luxon";

const defaultRefferalType: ReferralTypeModel = { referralTypeId: 0, name: "", rowVersion: "" }

const defaultClient: IClientModel = {
    clientId: 0,
    firstName: "",
    lastName: "",
    emailAddress: "",
    emailAddressNotAvailable: false,
    cellPhone: "",
    homePhone: "",
    alternatePhone: "",
    address: "",
    address2: "",
    city: "",
    stateId: undefined,
    state: undefined,
    pronoun: undefined,
    primaryHospital: undefined,
    relationshipType: undefined,
    county: "",
    zipCode: "",
    isOKToSMS: false,
    palsNumber: "",
    dateCreated: "",
    dateUpdated: "",
    lastVisitDate: "",
    firstVisitDate: "",
    alternatePetParentName: "",
    pets: [],
    referralTypeId: undefined,
    referralType: defaultRefferalType,
    isDeleted: false,
    pgrMasterId: "",
    businessName: "",
    isActive: true,
    hospitalId: 1203,
    pronounId: undefined,
    relationshipTypeId: undefined,
    rowVersion: "",
    canSMS: undefined,
    canSMSDateUpdated: undefined,
    canSMSUpdatedUserTitle: undefined,
    canSMSUpdatedUserFirstName: undefined,
    canSMSUpdatedUserLastName: undefined,
    canCall: undefined,
    canCallDateUpdated: undefined,
    canCallUpdatedUserTitle: undefined,
    canCallUpdatedUserFirstName: undefined,
    canCallUpdatedUserLastName: undefined,
    nationwideId: undefined,
    notes: [],
    nationwideIdNotAvailable: undefined,
    clientAuthorizedUsers: []
}

export class ClientStore {
    @observable client: ClientModel | undefined = undefined;
    @observable loading: boolean = false;
    @observable appointments = observable.array<AppointmentListingModel>([]);

    constructor() {
        makeObservable(this, undefined, { autoBind: true });
    }

    load = flow(function* (this: ClientStore, clientId: string | undefined, loadAppointments?: boolean, startOfDayAtHospitalInUTCShift?: DateTime) {
        this.loading = true;
        this.client = clientId == "0" || clientId === undefined
            ? new ClientModel(defaultClient)
            : yield ClientService.getClient(clientId)
                .catch(NotificationStoreInstance.onError('Failed to fetch client info.', defaultClient));
        if (loadAppointments)
            yield this.loadAppointments(startOfDayAtHospitalInUTCShift);

        this.loading = false;
    });

    /**
     * Queues email communication request
     */
    sendEmailCommunication = flow(function* (this: ClientStore, emailRequest: IAddClientEmailRequest) {
        yield ClientService.addEmailCommunication(emailRequest)
            .then(NotificationStoreInstance.onSuccess("Email was successfully queued"))
            .catch(NotificationStoreInstance.onError('Failed to fetch client info.', defaultClient));
    });

    /**
    * Adds or update a client
    * @param this current ClientStore reference
    * @param client client model to create or update
    */
    upsertClient = flow(function* (this: ClientStore, client: ClientModel) {
        this.loading = true;

        this.client = client.clientId === 0
            ? yield ClientService.addClient(client)
                .then(NotificationStoreInstance.onSuccess("Client added"))
                .catch(NotificationStoreInstance.onError("Failed to add client.", client))
            : yield ClientService.updateClient(client)
                .then(NotificationStoreInstance.onSuccess("Client updated"))
                .catch(NotificationStoreInstance.onError("Failed to update client.", client))
        this.loading = false;
    });

    /**
     * Sets the primary hospital for the current client
     */
    @action.bound setPrimaryHospital(hospital: HospitalModel | undefined) {
        if (this.client === undefined) return;
        this.client.primaryHospital = hospital;
        this.client.hospitalId = hospital == null ? 0 : hospital.hospitalId
    }

    @action.bound clearClient() {
        this.client = undefined;
    }

    @action.bound resetClient() {
        this.client = new ClientModel(defaultClient);
    }

    @action.bound patchClientPets(pet: PetModel) {
        if (this.client === undefined) return;

        this.loading = true;
        // Remove current Pet from Client Pets
        const filteredItems = _.filter(this.client.pets, (currentPet) => { return currentPet.petId !== pet.petId });
        // Push updated pet into Client Pets
        filteredItems.push(pet);
        // Refresh Client
        this.client.pets = filteredItems;

        this.loading = false;
    }

    /**
     * Finds the earliest started visit and returns the visitId
     */
    activeVisitId(petId: number): number | undefined {
        if (this.appointments === null) return;

        // In Progress appointments take precedence here
        const inProgressVisits = chain(this.appointments)
            .filter(a => a.petId === petId && a.appointmentStatusId == AppointmentStatus.In_Progress)
            .orderBy(['inprogressstart']).value();

        if (!isEmpty(inProgressVisits)) {
            return first(inProgressVisits)?.visitId;
        }

        // If no In Progress appointments exist, grab the earliest started appointment in Admitted, Finalized, or Paid
        const activeVisits = chain(this.appointments)
            .filter(a => a.petId === petId
                && includes([AppointmentStatus.Admitted,
                AppointmentStatus.Finalized,
                AppointmentStatus.Paid], a.appointmentStatusId))
            .orderBy(['visitStarted']).value();

        return first(activeVisits)?.visitId;
    }

    public loadAppointments = flow(function* (this: ClientStore, startOfDayAtHospitalInUTCShift?: DateTime) {

        this.appointments = yield ClientService.getAppointments(this.client!.clientId, { showError: true }, startOfDayAtHospitalInUTCShift);

    });


    public upsertClientNote = flow(function* (this: ClientStore, note: IClientNoteModel) {
        yield ClientNoteService.upsertClientNote(note)
            .catch(NotificationStoreInstance.onError("Failed to upsert Client Note", note));

        yield this.load(note.clientId.toString(), true);
    });

    /**
     * Triggered when an appointment has been modified
    */
    @action.bound syncAppointmentUpdates(this: ClientStore, appointment: AppointmentListingModel) {
        //update the current appointments array with the new appointment
        this.appointments = this.appointments.map(
            item => item.reservationId === appointment.reservationId ? appointment : item
        ) as IObservableArray<AppointmentListingModel>;
    }
}
